import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Search, Users, Star, X, ExternalLink, FileText } from 'lucide-react';

export const CandidatsPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateTests, setCandidateTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(false);

  useEffect(() => {
    fetchCandidates();
  }, [search]);

  const fetchCandidates = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const response = await api.get('/candidates', { params });
      setCandidates(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des candidats');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    setTestsLoading(true);
    try {
      const response = await api.get(`/candidates/${encodeURIComponent(candidate.prenom)}/${encodeURIComponent(candidate.nom)}/tests`);
      setCandidateTests(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des tests');
      setCandidateTests([]);
    } finally {
      setTestsLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedCandidate(null);
    setCandidateTests([]);
  };

  return (
    <Layout title="Candidats" breadcrumbs={['Accueil', 'Candidats']}>
      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un candidat..."
            className="eva-input pl-10"
            data-testid="candidate-search-input"
          />
        </div>
      </div>

      {/* Candidates List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      ) : candidates.length === 0 ? (
        <div className="text-center py-12" data-testid="empty-state">
          <Users size={48} className="text-[#E9ECEF] mx-auto mb-4" />
          <p className="text-slate-400">{search ? 'Aucun candidat trouvé pour cette recherche' : 'Aucun candidat évalué'}</p>
        </div>
      ) : (
        <div className="eva-card" data-testid="candidates-list">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-slate-400" />
              <p className="text-sm text-slate-400">{candidates.length} candidat{candidates.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="space-y-0">
            {candidates.map((candidate, index) => (
              <div
                key={`${candidate.prenom}-${candidate.nom}`}
                className={`flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors ${
                  index !== candidates.length - 1 ? 'border-b border-white/10' : ''
                }`}
                onClick={() => handleSelectCandidate(candidate)}
                data-testid={`candidate-row-${candidate.prenom}-${candidate.nom}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-500 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-white">
                      {(candidate.prenom?.[0] || '').toUpperCase()}{(candidate.nom?.[0] || '').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      {candidate.prenom} {candidate.nom}
                    </p>
                    <p className="text-xs text-slate-400">
                      {candidate.test_count} test{candidate.test_count > 1 ? 's' : ''} passé{candidate.test_count > 1 ? 's' : ''}
                      {candidate.last_test_date && (
                        <> — Dernier : {new Date(candidate.last_test_date).toLocaleDateString('fr-FR')}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Meilleur score</p>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={14}
                          fill={s <= Math.round(candidate.score || 0) ? '#F59E0B' : '#1E293B'}
                          stroke={s <= Math.round(candidate.score || 0) ? '#F59E0B' : '#1E293B'}
                        />
                      ))}
                      <span className="text-sm font-medium text-white ml-1">
                        {(candidate.best_score || 0).toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Candidate Detail Modal */}
      {selectedCandidate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="candidate-detail-modal">
          <div className="bg-[#0B132B] rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto mx-4 shadow-xl">
            {/* Header */}
            <div className="sticky top-0 bg-[#0B132B] border-b border-white/10 p-6 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-500 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-white">
                    {(selectedCandidate.prenom?.[0] || '').toUpperCase()}{(selectedCandidate.nom?.[0] || '').toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    {selectedCandidate.prenom} {selectedCandidate.nom}
                  </h3>
                  <p className="text-sm text-slate-400">
                    {selectedCandidate.test_count} test{selectedCandidate.test_count > 1 ? 's' : ''} passé{selectedCandidate.test_count > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-white" data-testid="close-candidate-modal">
                <X size={24} />
              </button>
            </div>

            {/* Tests List */}
            <div className="p-6">
              {testsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-slate-400">Chargement des tests...</p>
                </div>
              ) : candidateTests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText size={32} className="text-[#E9ECEF] mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Aucun test trouvé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {candidateTests.map((test) => (
                    <Link
                      key={test.id}
                      to={`/rapport/${test.id}`}
                      onClick={closeModal}
                      className="block p-4 border border-white/10 rounded-lg hover:bg-white/5 hover:border-[#6C757D] transition-all group"
                      data-testid={`candidate-test-${test.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-white truncate">{test.fonction}</h4>
                            {test.niveau && (
                              <span className="text-xs px-2 py-0.5 bg-white/5 text-slate-400 rounded shrink-0">{test.niveau}</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400">
                            {new Date(test.date).toLocaleDateString('fr-FR')}
                            {test.client && ` — ${test.client}`}
                            {test.ref_ao && ` — AO : ${test.ref_ao}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <div className="flex items-center gap-0.5 mb-1">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} size={14}
                                  fill={s <= Math.round(test.average_score || 0) ? '#F59E0B' : '#1E293B'}
                                  stroke={s <= Math.round(test.average_score || 0) ? '#F59E0B' : '#1E293B'}
                                />
                              ))}
                            </div>
                            <p className="text-sm font-medium text-white">
                              {(test.average_score || 0).toFixed(1)}/10
                            </p>
                            {test.evaluation && (
                              <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                                test.evaluation === 'Très bon niveau' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20' :
                                test.evaluation === 'Au-dessus de la moyenne' ? 'bg-[#10B981]/15 text-[#10B981]' :
                                test.evaluation === 'Dans la moyenne' ? 'bg-[#F59E0B]/15 text-[#F59E0B]' :
                                'bg-[#EF4444]/15 text-[#EF4444]'
                              }`}>{test.evaluation}</span>
                            )}
                          </div>
                          <ExternalLink size={16} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
